import React from 'react';
import { FileText, ExternalLink, CheckCircle } from 'lucide-react';
import type { Report } from '../../hooks/useReports';
import ReportStatus from './ReportStatus';
import ReportDownloadButton from './ReportDownloadButton';

interface ReportCardProps {
  report: Report;
}

export default function ReportCard({ report }: ReportCardProps) {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Check both status and URL to ensure report is truly complete
  const isCompleted = report.status === 'completed' && report.output_report_pdf_full_url;
  const createdAt = new Date(report.created_at);
  const isOlderThanOneHour = (new Date() - createdAt) > 3600000;

  // Determine the status to display
  let displayStatus = report.status; // Default to the original status

  // Check if the report is pending and older than one hour
  if (report.status === 'pending' && isOlderThanOneHour) {
    displayStatus = 'failed'; // Set to 'failed' if conditions are met
  }

  return (
    <div className="bg-white rounded-lg shadow p-6 transition-all duration-200">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center">
          <FileText className="w-6 h-6 text-blue-600 mr-3" />
          <div>
            <h3 className="text-lg font-medium text-gray-900">
              {report.company_name_from_make || 'Interview Report'}
            </h3>
            <p className="text-sm text-gray-500">{formatDate(report.created_at)}</p>
          </div>
        </div>
        <ReportStatus status={displayStatus} />
      </div>

      <div className="space-y-4">
        <div className="space-y-3">
          {report.job_title_from_make && (
            <div className="text-sm text-gray-600">
              <b>Job Title:</b> {report.job_title_from_make}
            </div>
          )}
          {report.linkedin_url && (
            <a
              href={report.linkedin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              LinkedIn Profile
            </a>
          )}
          {report.job_url && (
            <a
              href={report.job_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              Job Posting
            </a>
          )}
        </div>

        {isCompleted && (
          <div className="pt-4 border-t animate-fadeIn">
            <div className="flex items-center justify-between">
              
              <ReportDownloadButton url={report.output_report_pdf_full_url} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}